import * as Sentry from "@sentry/react";
import type { IApiClient } from "@/lib/__shared__/apiClient";

const API_BASE = import.meta.env.VITE_USER_SERVER_URL;

export interface RewardEarned {
	totalSecondsEarned: number;
	totalSecondsUsed: number;
	referralCount: number;
	referralCode: string | null;
}

export interface RewardData {
	id: string;
	name: string;
	description: string;
	rewardSeconds: number;
	maxSeconds: number;
	earned: RewardEarned | null;
}

export interface GetRewardsResult {
	rewards: RewardData[];
	hasUnseenRewards: boolean;
}

export interface ReferralResult {
	isCredited: boolean;
	secondsEarned?: number;
	reason?: string;
}

export interface HasUnseenResult {
	hasUnseenRewards: boolean;
}

export interface IRewardsService {
	getRewards(): Promise<GetRewardsResult>;
	recordReferral(
		_code: string,
		_fingerprint: string,
		_landingPage: string,
		_userAgent: string,
	): Promise<ReferralResult>;
	markSeen(_rewardIds: string[]): Promise<void>;
	hasUnseen(): Promise<HasUnseenResult>;
}

export class RewardsService implements IRewardsService {
	private apiClient: IApiClient;

	constructor(apiClient: IApiClient) {
		this.apiClient = apiClient;
	}

	async getRewards(): Promise<GetRewardsResult> {
		try {
			const response = await this.apiClient.fetch(`${API_BASE}/rewards`, {
				method: "GET",
			});

			if (!response.ok) {
				const json = await response.json().catch(() => ({}));
				throw new Error(json.message ?? "Failed to fetch rewards.");
			}

			const json = await response.json();
			return json.data as GetRewardsResult;
		} catch (error) {
			Sentry.captureException(error);
			return { rewards: [], hasUnseenRewards: false };
		}
	}

	async recordReferral(
		_code: string,
		_fingerprint: string,
		_landingPage: string,
		_userAgent: string,
	): Promise<ReferralResult> {
		const response = await this.apiClient.fetch(
			`${API_BASE}/rewards/referral`,
			{
				method: "POST",
				body: JSON.stringify({
					referralCode: _code,
					fingerprint: _fingerprint,
					landingPage: _landingPage,
					userAgent: _userAgent,
				}),
			},
		);

		if (!response.ok) {
			const json = await response.json().catch(() => ({}));
			throw new Error(json.message ?? "Failed to record referral.");
		}

		const json = await response.json();
		return json.data as ReferralResult;
	}

	async markSeen(_rewardIds: string[]): Promise<void> {
		try {
			const response = await this.apiClient.fetch(
				`${API_BASE}/rewards/mark-seen`,
				{
					method: "POST",
					body: JSON.stringify({ rewardIds: _rewardIds }),
				},
			);

			if (!response.ok) {
				const json = await response.json().catch(() => ({}));
				throw new Error(json.message ?? "Failed to mark rewards as seen.");
			}
		} catch (error) {
			Sentry.captureException(error);
		}
	}

	async hasUnseen(): Promise<HasUnseenResult> {
		try {
			const response = await this.apiClient.fetch(
				`${API_BASE}/rewards/has-unseen`,
				{
					method: "GET",
				},
			);

			if (!response.ok) {
				const json = await response.json().catch(() => ({}));
				throw new Error(json.message ?? "Failed to check unseen rewards.");
			}

			const json = await response.json();
			return json.data as HasUnseenResult;
		} catch (error) {
			Sentry.captureException(error);
			return { hasUnseenRewards: false };
		}
	}
}
